/**
 * Admin: fit a letter paper's writing area. The first guess comes from
 * `detectWritingArea`; the admin drags the rectangle's edges (or the whole
 * rectangle) until the words sit only on clear paper, picks an ink, and sees
 * sample text laid out exactly as a reader will before saving.
 */

import { useCallback, useRef, useState } from 'react';
import type { Stationery, WritingArea } from '../letters/api';
import { LetterPaper } from '../stationery/LetterPaper';

const SAMPLE = `Dear Maya,

The swifts have gone and the starlings have taken over the wires. I walked the long way round the harbour today and thought of the letter you wrote about the lighthouse keeper, and of the band that played on the pier until the rain came in sideways.

There is nothing urgent in any of this, which is exactly how I like it. Write when the kettle's on.

With love,
Leon`;

const INKS = [
  { label: 'Dark ink', value: '#2C2420' },
  { label: 'Navy', value: '#1B2A4A' },
  { label: 'Sepia', value: '#5B3A29' },
  { label: 'Black', value: '#111111' },
];

type Edge = 'top' | 'right' | 'bottom' | 'left' | 'move';
const MIN = 0.2;
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export interface PaperDraft {
  name: string;
  area: WritingArea;
  ink: string;
}

export function PaperEditor({
  imageUrl,
  width,
  height,
  initial,
  saving,
  saveLabel,
  onSave,
  onCancel,
}: {
  imageUrl: string;
  width: number;
  height: number;
  initial: PaperDraft;
  saving: boolean;
  saveLabel: string;
  onSave: (draft: PaperDraft) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial.name);
  const [area, setArea] = useState<WritingArea>(initial.area);
  const [ink, setInk] = useState(initial.ink);
  const frame = useRef<HTMLDivElement>(null);
  const drag = useRef<{ edge: Edge; x: number; y: number; start: WritingArea } | null>(null);

  const onDown = (edge: Edge) => (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { edge, x: e.clientX, y: e.clientY, start: area };
  };

  const onMove = useCallback((e: React.PointerEvent) => {
    const d = drag.current;
    const box = frame.current?.getBoundingClientRect();
    if (!d || !box) return;
    const dx = (e.clientX - d.x) / box.width;
    const dy = (e.clientY - d.y) / box.height;
    const s = d.start;
    let next = { ...s };
    switch (d.edge) {
      case 'top':
        next.top = clamp(s.top + dy, 0, 1 - s.bottom - MIN);
        break;
      case 'bottom':
        next.bottom = clamp(s.bottom - dy, 0, 1 - s.top - MIN);
        break;
      case 'left':
        next.left = clamp(s.left + dx, 0, 1 - s.right - MIN);
        break;
      case 'right':
        next.right = clamp(s.right - dx, 0, 1 - s.left - MIN);
        break;
      case 'move': {
        const mx = clamp(dx, -s.left, s.right);
        const my = clamp(dy, -s.top, s.bottom);
        next = { top: s.top + my, bottom: s.bottom - my, left: s.left + mx, right: s.right - mx };
      }
    }
    const r = (n: number) => Math.round(n * 1000) / 1000;
    setArea({ top: r(next.top), right: r(next.right), bottom: r(next.bottom), left: r(next.left) });
  }, []);

  const onUp = () => {
    drag.current = null;
  };

  const preview: Stationery = { id: 'preview', kind: 'paper', name: name || 'Paper', imageUrl, width, height, area, ink, scale: 1 };
  const rect = {
    top: `${area.top * 100}%`,
    left: `${area.left * 100}%`,
    right: `${area.right * 100}%`,
    bottom: `${area.bottom * 100}%`,
  };

  return (
    <div className="paper-editor">
      <div className="paper-editor__cols">
        <div>
          <p className="admin-setting__label">Writing area</p>
          <p className="admin-setting__help">Drag the edges, or the box itself, so words sit only on clear paper.</p>
          <div
            className="paper-editor__frame"
            ref={frame}
            style={{ aspectRatio: `${width} / ${height}`, backgroundImage: `url(${imageUrl})` }}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
          >
            <div className="paper-editor__area" style={rect} onPointerDown={onDown('move')}>
              {(['top', 'right', 'bottom', 'left'] as const).map((edge) => (
                <span
                  key={edge}
                  className={`paper-editor__handle paper-editor__handle--${edge}`}
                  onPointerDown={onDown(edge)}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          <p className="admin-setting__label">How a letter looks on it</p>
          <div className="paper-editor__preview">
            <LetterPaper paper={preview} text={SAMPLE} />
          </div>
        </div>
      </div>

      <div className="admin-stamps__new">
        <label className="admin-setting__input">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="Jazz club" />
        </label>
        <span className="admin-setting__input">
          <span>Ink</span>
          {INKS.map((i) => (
            <button
              key={i.value}
              type="button"
              className={`paper-editor__ink${ink.toLowerCase() === i.value.toLowerCase() ? ' is-on' : ''}`}
              style={{ background: i.value }}
              title={i.label}
              aria-label={i.label}
              aria-pressed={ink.toLowerCase() === i.value.toLowerCase()}
              onClick={() => setInk(i.value)}
            />
          ))}
          <input type="color" value={ink} onChange={(e) => setInk(e.target.value)} aria-label="Any ink colour" />
        </span>
      </div>
      <div className="admin-stamps__actions">
        <button
          type="button"
          className="btn btn--primary"
          disabled={saving || !name.trim()}
          onClick={() => onSave({ name: name.trim(), area, ink })}
        >
          {saving ? 'Saving…' : saveLabel}
        </button>
        <button type="button" className="btn btn--ghost" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
